import {
  Flex,
  Heading,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  Icon,
  IconToggle,
} from "@repo/ping-design";
import SOVDataRelevanceCard from "../SOVDataRelevanceCard";
import "../SOVDataRelevanceGraphComponent.scss";
import { memo, useMemo, useCallback, type ReactNode } from "react";
import {
  ReportSectionData,
  ReportSubSectionData,
} from "../../../ts-types/reportTypes";
import { formatCurrency, formatWithCommas } from "../../../utils/formatters";
import { useAnalytics, POSTHOG_EVENTS } from "@repo/ping-react-js";

const RANGE_FIELD_REGEX = /__(gt|gte|lt|lte|gt_or_null|lte_or_null)$/;

type FiltersMap = Record<string, string | string[]>;

const clampPercentWidth = (width: number) => {
  if (width > 100) return 100;
  if (width > 0 && width < 1) return 1;
  return width;
};

const extractLayerParams = (
  filters: Array<{ field: string; value: any }>,
): { attachVal?: number | string; layerLimitVal?: number | string } => {
  let attachVal: number | string | undefined;
  let layerLimitVal: number | string | undefined;
  for (const filter of filters) {
    if (filter.field === "attach") attachVal = filter.value ?? "";
    else if (filter.field === "layer_limit") layerLimitVal = filter.value ?? "";
    if (attachVal !== undefined && layerLimitVal !== undefined) break;
  }
  return { attachVal, layerLimitVal };
};

type PingExposureGraphComponentProps = {
  isLoading?: boolean;
  filters?: FiltersMap;
  setFilter?: (field: string, value: string | string[]) => void;
  clearFilter?: (field: string) => void;
  attach?: number | string | null;
  layerLimit?: number | string | null;
  onSetCustomLayer?: (params: {
    attach: number | string;
    layerLimit: number | string;
  }) => void;
} & ReportSectionData;

type PingExposureGraphComponentRowProps = {
  data: ReportSubSectionData;
  isLoading?: boolean;
  hasCountData?: boolean;
  filters?: FiltersMap;
  setFilter?: (field: string, value: string | string[]) => void;
  clearFilter?: (field: string) => void;
  attach?: number | string | null;
  layerLimit?: number | string | null;
  onSetCustomLayer?: (params: {
    attach: number | string;
    layerLimit: number | string;
  }) => void;
  allRowsData?: ReportSubSectionData[];
};

const computeRowKey = (data: ReportSubSectionData) => {
  const base = data.field ?? data.label ?? "row";
  const filtersSig = JSON.stringify(data.filter_data?.filters ?? []);
  return `${base}:${filtersSig}`;
};

const getRequiredByField = (rowData: ReportSubSectionData) => {
  const requiredByField = new Map<string, string[]>();
  for (const f of rowData.filter_data?.filters ?? []) {
    const vals = Array.isArray(f.value)
      ? (f.value as any[]).map((v) => String(v))
      : [String(f.value)];
    if (!requiredByField.has(f.field)) requiredByField.set(f.field, []);
    requiredByField.get(f.field)!.push(...vals);
  }
  return requiredByField;
};

const isRowToggled = (
  rowData: ReportSubSectionData,
  filters: FiltersMap,
  attach?: number | string | null,
  layerLimit?: number | string | null,
) => {
  if (
    !rowData.filter_data?.filters ||
    rowData.filter_data.filters.length === 0
  ) {
    return false;
  }

  const isLayerAnalysis = rowData.filter_data.type === "layer_analysis";

  if (isLayerAnalysis) {
    const { attachVal, layerLimitVal } = extractLayerParams(
      rowData.filter_data.filters as Array<{ field: string; value: any }>,
    );
    return attach === attachVal && layerLimit === layerLimitVal;
  }

  const requiredByField = getRequiredByField(rowData);

  const fieldMatches = (field: string, expectedVals: (string | number)[]) => {
    const current = filters[field];
    const isRange = RANGE_FIELD_REGEX.test(field);
    if (current === undefined) return false;
    const normalize = (v: any) => (isRange ? Number(v) : String(v));
    const expected = expectedVals.map(normalize);
    if (Array.isArray(current)) {
      const currentNorm = current.map(normalize);
      return expected.every((v) => currentNorm.includes(v));
    }
    if (expected.length > 1) return false;
    return normalize(current) === expected[0];
  };

  for (const [field, expectedVals] of requiredByField.entries()) {
    if (!fieldMatches(field, expectedVals)) return false;
  }
  return true;
};

const PingExposureGraphComponentRow = ({
  data,
  isLoading,
  hasCountData = false,
  filters = {},
  setFilter,
  clearFilter,
  attach,
  layerLimit,
  onSetCustomLayer,
  allRowsData,
}: PingExposureGraphComponentRowProps) => {
  const { captureEvent } = useAnalytics();
  const percentValue = typeof data.percent === "number" ? data.percent : 0;
  const toggled = useMemo(
    () => isRowToggled(data, filters, attach, layerLimit),
    [attach, data, filters, layerLimit],
  );
  const hasHighExposure = percentValue >= 20;
  const classPrefix = hasCountData ? "CountData" : "Data";
  const percentageBarClassName = `SOVDataRelevanceGraphComponent__${classPrefix}__Percentage${
    hasHighExposure
      ? ` SOVDataRelevanceGraphComponent__${classPrefix}__Percentage--Warning`
      : ""
  }`;

  const percentWidth = clampPercentWidth(Number(percentValue.toFixed(2)));

  const handleToggle = useCallback(
    (rowData: ReportSubSectionData, toggle: boolean) => {
      const localRowKey = computeRowKey(rowData);

      captureEvent(POSTHOG_EVENTS.DATA_RELEVANCE_FILTER_TOGGLED, {
        source: "ping_crucial_exposure",
        row_label: rowData.label,
        field: rowData.field,
        filter_type: rowData.filter_data?.type,
        toggled: toggle,
      });

      const isLayerAnalysis = rowData.filter_data?.type === "layer_analysis";
      if (isLayerAnalysis) {
        if (toggle) {
          const { attachVal, layerLimitVal } = extractLayerParams(
            rowData.filter_data!.filters as Array<{
              field: string;
              value: any;
            }>,
          );
          onSetCustomLayer?.({
            attach: attachVal ?? "",
            layerLimit: layerLimitVal ?? "",
          });
        } else {
          onSetCustomLayer?.({ attach: "", layerLimit: "" });
        }
        return;
      }

      const isSingleFilter = rowData.filter_data?.type === "single";
      if (isSingleFilter) {
        for (const f of rowData.filter_data?.filters ?? []) {
          if (toggle) {
            setFilter?.(f.field, f.value);
          } else {
            clearFilter?.(f.field);
          }
        }
        return;
      }

      const requiredByField = getRequiredByField(rowData);

      if (toggle) {
        for (const [field, reqVals] of requiredByField.entries()) {
          const current = filters[field];
          const nextSet = new Set<string>();
          if (Array.isArray(current)) {
            current.forEach((v) => nextSet.add(String(v)));
          } else if (current !== undefined) {
            nextSet.add(String(current));
          }
          reqVals.forEach((v) => nextSet.add(String(v)));
          setFilter?.(field, Array.from(nextSet));
        }
      } else {
        for (const [field, removeVals] of requiredByField.entries()) {
          const current = filters[field];
          if (current === undefined) continue;

          const safeToRemove = new Set(removeVals);
          allRowsData?.forEach((rd) => {
            if (computeRowKey(rd) === localRowKey) return;
            if (isRowToggled(rd, filters, attach, layerLimit)) {
              const otherReq = getRequiredByField(rd);
              const otherVals = otherReq.get(field) || [];
              otherVals.forEach((v) => {
                if (safeToRemove.has(v)) safeToRemove.delete(v);
              });
            }
          });

          let remaining: string[] = [];
          if (Array.isArray(current)) {
            remaining = current.filter((v) => !safeToRemove.has(String(v)));
          } else if (current !== undefined) {
            if (!safeToRemove.has(String(current))) {
              remaining = [String(current)];
            }
          }

          if (remaining.length === 0) {
            clearFilter?.(field);
          } else {
            setFilter?.(field, remaining);
          }
        }
      }
    },
    [
      attach,
      captureEvent,
      clearFilter,
      filters,
      layerLimit,
      onSetCustomLayer,
      setFilter,
      allRowsData,
    ],
  );

  const hasRowFilters = Boolean(data.filter_data?.filters?.length);

  const filterComponent = useMemo(
    () =>
      hasRowFilters ? (
        <IconToggle
          iconName="ListFilterPlus"
          size="xs"
          toggled={toggled}
          onChange={(toggle: boolean) => handleToggle(data, toggle)}
        />
      ) : null,
    [data, toggled, handleToggle, hasRowFilters],
  );

  const labelContent = useMemo(
    () =>
      data.info_tip ? (
        <Tooltip>
          <TooltipTrigger>
            <span className="SOVDataRelevanceGraphComponent__LabelWithTooltip">
              {data.label}
              <Icon iconName="Info" size={14} />
            </span>
          </TooltipTrigger>
          <TooltipContent>{data.info_tip}</TooltipContent>
        </Tooltip>
      ) : (
        data.label
      ),
    [data.info_tip, data.label],
  );

  return (
    <Flex
      className={`SOVDataRelevanceGraphComponent__${classPrefix}${
        toggled ? " SOVDataRelevanceGraphComponent--Selected" : ""
      }`}
      gap="2"
      align="center"
    >
      {filterComponent}
      <div className={`SOVDataRelevanceGraphComponent__${classPrefix}__Label`}>
        {labelContent}
      </div>
      {hasCountData && (
        <div
          className={`SOVDataRelevanceGraphComponent__${classPrefix}__Count`}
        >
          {formatWithCommas(data.count ?? 0)}
        </div>
      )}
      <div className={`SOVDataRelevanceGraphComponent__${classPrefix}__Value`}>
        {isLoading && (
          <div className="SOVDataRelevanceGraphComponent__Loading" />
        )}
        {data.tiv === 0 && (
          <div className="SOVDataRelevanceGraphComponent__NoDataBackground" />
        )}
        <div
          className={percentageBarClassName}
          style={{
            width: `${percentWidth}%`,
          }}
        />
        <Flex
          className={`SOVDataRelevanceGraphComponent__${classPrefix}__Text`}
          justify="end"
        >
          <div
            className={`SOVDataRelevanceGraphComponent__${classPrefix}__Text__Value`}
          >
            {formatCurrency(data.tiv, true)}
          </div>
          <div
            className={`SOVDataRelevanceGraphComponent__${classPrefix}__Text__Percentage`}
          >
            {percentValue.toFixed(0)}%
          </div>
        </Flex>
      </div>
    </Flex>
  );
};

const PingExposureGraphComponent = ({
  title,
  info_tip,
  data,
  isLoading,
  icon,
  filters,
  setFilter,
  clearFilter,
  attach,
  layerLimit,
  onSetCustomLayer,
  warningMessage,
}: PingExposureGraphComponentProps & {
  icon?: ReactNode;
  warningMessage?: string;
}) => {
  const hasCountData = useMemo(() => {
    return data?.some((item) => "count" in item);
  }, [data]);

  const titleContent = useMemo(
    () =>
      info_tip ? (
        <Tooltip>
          <TooltipTrigger>
            <span className="SOVDataRelevanceGraphComponent__TitleWithTooltip">
              {title}
              <Icon iconName="Info" size={14} />
            </span>
          </TooltipTrigger>
          <TooltipContent>{info_tip}</TooltipContent>
        </Tooltip>
      ) : (
        title
      ),
    [info_tip, title],
  );

  const rowsComponent = useMemo(() => {
    if (!data?.length) {
      return (
        <Flex
          justify="center"
          align="center"
          className="SOVDataRelevanceGraphComponent__NoData"
        >
          No Data Available
        </Flex>
      );
    }
    return data?.map((item) => (
      <PingExposureGraphComponentRow
        key={computeRowKey(item)}
        data={item}
        hasCountData={hasCountData}
        isLoading={isLoading}
        filters={filters}
        setFilter={setFilter}
        clearFilter={clearFilter}
        attach={attach}
        layerLimit={layerLimit}
        onSetCustomLayer={onSetCustomLayer}
        allRowsData={data}
      />
    ));
  }, [
    attach,
    clearFilter,
    data,
    filters,
    hasCountData,
    isLoading,
    layerLimit,
    onSetCustomLayer,
    setFilter,
  ]);

  return (
    <SOVDataRelevanceCard>
      <Flex
        className="SOVDataRelevanceGraphComponent"
        direction="column"
        gap={hasCountData ? "none" : "4"}
      >
        <Heading level={3} size="sm">
          <Flex align="center" gap="2" justify="space-between" style={{ flexWrap: "wrap" }}>
            <Flex align="center" gap="2">
              {icon}
              {titleContent}
            </Flex>
            {warningMessage && (
              <span className="SOVDataRelevanceGraphComponent__WarningMessage">
                {warningMessage}
              </span>
            )}
          </Flex>
        </Heading>
        <Flex direction="column" gap="1">
          {hasCountData && (
            <Flex
              className="SOVDataRelevanceGraphComponent__CountData"
              gap="2"
              align="center"
            >
              <div className="SOVDataRelevanceGraphComponent__BlankFilter" />
              <div className="SOVDataRelevanceGraphComponent__CountData__Label"></div>
              <div className="SOVDataRelevanceGraphComponent__CountData__CountTitle">
                Count
              </div>
              <div className="SOVDataRelevanceGraphComponent__CountData__ValueTitle">
                TIV
              </div>
            </Flex>
          )}
          {rowsComponent}
        </Flex>
      </Flex>
    </SOVDataRelevanceCard>
  );
};

export default memo(PingExposureGraphComponent);
